import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, Zap, Compass, Bot, BadgeCheck, TrendingUp, ScrollText, Quote, Brain } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import LeadForm from "../components/LeadForm";
import Eyebrow from "../components/Eyebrow";
import WhyUsHub from "../components/WhyUsHub";
import { HOME } from "../constants/testIds";

const serviceIcons = [Sparkles, Zap, Compass, Bot];

const HERO_IMAGE = "https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80";
// Professional Latina/Latino executive woman in modern office with laptop — humanizes brand
// Fallback: business consultation
const HERO_IMAGE_FALLBACK = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80";

// Testimonial avatars (diverse executives)
const AVATARS = [
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function HomePage() {
  const { t } = useTranslation();
  const services = t("home.services.items", { returnObjects: true });
  const stats = t("home.stats.items", { returnObjects: true });
  const testimonials = t("home.testimonials.items", { returnObjects: true });

  return (
    <main>
      {/* ============ HERO — split layout, gradient bg, no particles ============ */}
      <section className="relative overflow-hidden hero-bg-light dark:hero-bg-dark">
        <div className="container-page pt-16 md:pt-24 pb-20 md:pb-32 relative">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: text */}
            <div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Eyebrow className="mb-6">{t("home.hero.eyebrow")}</Eyebrow>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] text-balance"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
              >
                {t("home.hero.title")}
              </motion.h1>

              <motion.p
                className="mt-7 text-base md:text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {t("home.hero.subtitle")}
              </motion.p>

              <motion.div
                className="mt-10 flex flex-col sm:flex-row gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                <Link to="/contacto" data-testid={HOME.heroPrimary} className="btn-primary">
                  {t("home.hero.ctaPrimary")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/consultorias" data-testid={HOME.heroSecondary} className="btn-ghost-line">
                  {t("home.hero.ctaSecondary")}
                </Link>
              </motion.div>

              {/* Trust badges */}
              <motion.div
                className="mt-10 flex items-center gap-4 text-xs text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
              >
                <div className="flex -space-x-3">
                  {AVATARS.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover border-2 border-background"
                      loading="lazy"
                    />
                  ))}
                </div>
                <span className="leading-tight">
                  <span className="font-semibold text-foreground">+40 directivos</span> ya han confiado en nosotros
                </span>
              </motion.div>
            </div>

            {/* Right: image card */}
            <motion.div
              initial={{ opacity: 0, x: 30, scale: 0.97 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
              className="relative"
            >
              <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] xl:aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
                <img
                  src={HERO_IMAGE}
                  alt="Profesionales aplicando IA en empresas"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = HERO_IMAGE_FALLBACK; }}
                />
                {/* gradient overlay for cohesion with dark theme */}
                <div className="absolute inset-0 bg-gradient-to-tr from-dc-navy/40 via-transparent to-transparent dark:from-dc-navy/70" />
              </div>

              {/* Floating badge: AI Brain */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="absolute -left-3 sm:-left-6 bottom-6 sm:bottom-10 glass-card p-4 sm:p-5 max-w-[220px] hidden sm:block"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-dc-amber/15 text-dc-amber flex items-center justify-center">
                    <Brain className="w-5 h-5" />
                  </div>
                  <div className="text-xs">
                    <div className="font-semibold text-foreground">IA Aplicada</div>
                    <div className="text-muted-foreground">A tu industria</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating badge: ROI */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.85 }}
                className="absolute -right-3 sm:-right-6 top-6 sm:top-10 glass-card p-4 sm:p-5 hidden sm:block"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div className="text-xs">
                    <div className="font-semibold text-foreground">+ROI medible</div>
                    <div className="text-muted-foreground">en 90 días</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="section-y border-t border-border">
        <div className="container-page">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16 items-end mb-14">
            <motion.div {...fadeUp} className="lg:col-span-2">
              <Eyebrow className="mb-4">{t("home.stats.title")}</Eyebrow>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl">
                {t("home.stats.subtitle")}
              </h2>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {stats.map((s, i) => {
              // Alternate accent: orange / cyan / orange to balance the palette
              const isCyan = i === 1;
              return (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                  className="glass-card p-8 md:p-10"
                >
                  <div
                    data-testid={HOME.statValue(i)}
                    className={`text-5xl md:text-6xl font-bold tracking-tight ${
                      isCyan
                        ? "text-dc-cyan-light dark:text-dc-cyan"
                        : "text-dc-amber-light dark:text-dc-amber"
                    }`}
                  >
                    <AnimatedCounter value={s.value} />
                  </div>
                  <p className="mt-4 text-muted-foreground text-base">{s.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="section-y bg-secondary/30">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Eyebrow className="mb-4">{t("home.services.eyebrow")}</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              {t("home.services.title")}
            </h2>
            <p className="text-muted-foreground text-lg">{t("home.services.subtitle")}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {services.map((s, i) => {
              const Icon = serviceIcons[i] || Sparkles;
              return (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                >
                  <Link
                    to={s.link}
                    data-testid={HOME.serviceCard(i)}
                    className="group glass-card p-8 md:p-10 block h-full"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-dc-amber/15 group-hover:text-dc-amber-light dark:group-hover:text-dc-amber transition-colors">
                        <Icon className="w-6 h-6" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-dc-amber-light dark:group-hover:text-dc-amber group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-semibold mb-3">{s.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{s.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHY US — animated hub ============ */}
      <WhyUsHub />

      {/* ============ TESTIMONIALS ============ */}
      <section className="section-y bg-secondary/30 border-y border-border">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Eyebrow className="mb-4">{t("home.testimonials.eyebrow")}</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              {t("home.testimonials.title")}
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((tst, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="glass-card p-8"
              >
                <Quote className="w-8 h-8 text-dc-amber-light dark:text-dc-amber mb-4" />
                <p className="text-base md:text-lg leading-relaxed mb-6">{tst.quote}</p>
                <div className="pt-4 border-t border-border flex items-center gap-3">
                  <img
                    src={AVATARS[i % AVATARS.length]}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold text-sm">{tst.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{tst.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="section-y">
        <div className="container-page">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <motion.div {...fadeUp}>
              <Eyebrow className="mb-4">
                <ScrollText className="inline w-4 h-4 mr-1 -mt-1" />
                {t("home.ctaFinal.cta")}
              </Eyebrow>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                {t("home.ctaFinal.title")}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t("home.ctaFinal.subtitle")}
              </p>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-dc-amber-light dark:text-dc-amber" /> Llamada gratuita de 30 minutos</li>
                <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-dc-amber-light dark:text-dc-amber" /> Diagnóstico inicial sin compromiso</li>
                <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-dc-amber-light dark:text-dc-amber" /> Confidencialidad garantizada</li>
              </ul>
            </motion.div>
            <motion.div {...fadeUp} className="glass-card p-8 md:p-10">
              <LeadForm pageOrigin="home" />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
